import React, { useEffect, useMemo, useState } from "react";
import { Checkbox } from "@mui/material";
import { translate } from "../../../../../../common/providers";
import {
	createNotification,
	CustomTable,
	CustomTableBody,
	CustomTableCell,
	CustomTableHead,
	CustomTableRow,
	OptionsPanelContainer,
	SearchInput,
	SidePanel,
	SidePanelContent,
} from "../../../../../../common/components";
import { useApi } from "../../../../../../common/hooks";
import { AiAgentsService } from "../../../../../../api";
import styles from "./AgentSpawnableSidePanel.module.css";
import { isNonEmptyArray } from "../../../../../../common/utils";

const getAgentLabel = (agent) => agent?.title || agent?.prompt || String(agent?.id || "");

// Paged list endpoints resolve to { contents, hasMore } (see InfiniteList2); the
// spawnable-agents endpoint resolves to a plain array of ids.
const normalizeAgentList = (response) => (Array.isArray(response?.contents) ? response.contents : []);

const normalizeSpawnableIds = (response) => (Array.isArray(response) ? response : []);

export default function AgentSpawnableSidePanel({ agentId, agentName, open, onClose, onRefresh }) {
	const [allAgents, setAllAgents] = useState([]);
	const [isSaving, setIsSaving] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [searchInput, setSearchInput] = useState("");
	const [currentSelectedAgents, setCurrentSelectedAgents] = useState([]);
	const [initialSelectedAgents, setInitialSelectedAgents] = useState([]);
	const { call: getSpawnableAgents } = useApi(AiAgentsService.getSpawnableAgents);
	const { call: getDefinitionList } = useApi(AiAgentsService.getDefinitionList);
	const { call: linkSpawnableAgent } = useApi(AiAgentsService.linkSpawnableAgent);
	const { call: unlinkSpawnableAgent } = useApi(AiAgentsService.unlinkSpawnableAgent);

	useEffect(() => {
		if (!open || !agentId) {
			return undefined;
		}

		let isActive = true;
		setIsLoading(true);
		getDefinitionList({ page: 0, size: 200, sort: "title;asc" })
			.then((definitionList) => {
				if (!isActive) {
					return undefined;
				}
				return getSpawnableAgents({ parentAgentId: agentId }).then((spawnableIds) => ({
					definitionList,
					spawnableIds,
				}));
			})
			.then((result) => {
				if (!isActive || !result) {
					return undefined;
				}
				const { definitionList, spawnableIds } = result;
				const selectedIds = normalizeSpawnableIds(spawnableIds);
				const listAgents = normalizeAgentList(definitionList).filter(
					(agent) => agent?.id && agent.id !== agentId
				);
				const sortedAgents = [...listAgents].sort((a, b) => {
					const aPriority = selectedIds.includes(a.id);
					const bPriority = selectedIds.includes(b.id);
					if (aPriority && !bPriority) {
						return -1;
					}
					if (!aPriority && bPriority) {
						return 1;
					}
					return getAgentLabel(a).localeCompare(getAgentLabel(b));
				});
				setCurrentSelectedAgents(selectedIds);
				setInitialSelectedAgents(selectedIds);
				setAllAgents(sortedAgents);
				setSearchInput("");

				return undefined;
			})
			.catch(console.error)
			.finally(() => {
				if (isActive) {
					setIsLoading(false);
				}
			});

		return () => {
			isActive = false;
		};
	}, [open, agentId, getDefinitionList, getSpawnableAgents]);

	const visibleAgents = useMemo(() => {
		const query = searchInput.toLowerCase();
		if (!query) {
			return allAgents;
		}
		return allAgents.filter((agent) => {
			const title = String(agent?.title || "").toLowerCase();
			const prompt = String(agent?.prompt || "").toLowerCase();
			return title.includes(query) || prompt.includes(query);
		});
	}, [allAgents, searchInput]);

	const allChecked = useMemo(
		() =>
			isNonEmptyArray(visibleAgents) && visibleAgents.every((agent) => currentSelectedAgents.includes(agent.id)),
		[visibleAgents, currentSelectedAgents]
	);

	const indeterminateAllChecked = useMemo(
		() => visibleAgents.some((agent) => currentSelectedAgents.includes(agent.id)),
		[visibleAgents, currentSelectedAgents]
	);

	const handleCheckAgent = (id) => {
		if (currentSelectedAgents.includes(id)) {
			setCurrentSelectedAgents((prev) => prev.filter((agentIdToRemove) => agentIdToRemove !== id));
		} else {
			setCurrentSelectedAgents((prev) => [...prev, id]);
		}
	};

	const handleCheckAll = () => {
		const visibleAgentIds = visibleAgents.map((agent) => agent.id);
		if (allChecked) {
			setCurrentSelectedAgents((prev) =>
				prev.filter((agentIdToKeep) => !visibleAgentIds.includes(agentIdToKeep))
			);
		} else {
			setCurrentSelectedAgents((prev) => [...new Set([...prev, ...visibleAgentIds])]);
		}
	};

	const handleSubmit = () => {
		setIsSaving(true);
		const toAdd = currentSelectedAgents.filter((id) => !initialSelectedAgents.includes(id));
		const toRemove = initialSelectedAgents.filter((id) => !currentSelectedAgents.includes(id));
		Promise.all([
			...toAdd.map((spawnedAgentId) => linkSpawnableAgent({ parentAgentId: agentId, spawnedAgentId })),
			...toRemove.map((spawnedAgentId) => unlinkSpawnableAgent({ parentAgentId: agentId, spawnedAgentId })),
		])
			.then(() => {
				setInitialSelectedAgents(currentSelectedAgents);
				setIsSaving(false);
				createNotification({ type: "success", message: translate("dsi.agents.spawnable.save.success") });
				onRefresh?.();
			})
			.catch((error) => {
				console.error(error);
				createNotification({ type: "error", message: translate("dsi.agents.spawnable.save.failure") });
				// Some link/unlink calls may have succeeded — resync from the backend instead
				// of leaving the panel pretending nothing happened.
				return getSpawnableAgents({ parentAgentId: agentId })
					.then((spawnableIds) => {
						const selectedIds = normalizeSpawnableIds(spawnableIds);
						setInitialSelectedAgents(selectedIds);
						setCurrentSelectedAgents(selectedIds);
					})
					.catch(console.error)
					.finally(() => setIsSaving(false));
			});
	};

	const handleChangeSearch = (e) => {
		setSearchInput(e.target.value);
	};

	const handleClearSearch = () => {
		setSearchInput("");
	};

	const handleClose = () => {
		onClose?.();
		setSearchInput("");
		setAllAgents([]);
		setCurrentSelectedAgents([]);
		setInitialSelectedAgents([]);
		setIsLoading(false);
		setIsSaving(false);
	};

	const rowRenderer = (agent) => {
		const promptPreview = String(agent?.prompt || "");
		return (
			<CustomTableRow key={agent.id}>
				<CustomTableCell>
					<Checkbox
						checked={currentSelectedAgents.includes(agent.id)}
						disabled={isSaving || isLoading}
						onChange={() => handleCheckAgent(agent.id)}
					/>
				</CustomTableCell>
				<CustomTableCell>{agent.title}</CustomTableCell>
				<CustomTableCell className={styles.promptCell} title={promptPreview}>
					{promptPreview}
				</CustomTableCell>
			</CustomTableRow>
		);
	};

	return (
		<SidePanel disableBackdropClick open={open} size={90} onClose={handleClose}>
			<SidePanelContent
				className={styles.content}
				title={translate("dsi.agents.spawnable.side-panel.title", { agentName })}
				onClose={handleClose}
			>
				<OptionsPanelContainer
					buttonText={(isSaving && translate("common:btn.saving")) || translate("common:btn.save")}
					disabledPrimaryButton={isSaving || isLoading}
					title={translate("dsi.agents.spawnable")}
					onClick={handleSubmit}
				>
					<div className={styles.search}>
						<SearchInput
							placeholder={translate("dsi.agents.spawnable.search.placeholder")}
							value={searchInput}
							onChange={handleChangeSearch}
							onClearSearch={handleClearSearch}
						/>
					</div>
					<CustomTable dense>
						<CustomTableHead>
							<CustomTableRow className={styles.header}>
								<CustomTableCell>
									<Checkbox
										checked={allChecked}
										disabled={isSaving || isLoading}
										indeterminate={!allChecked && indeterminateAllChecked}
										onChange={handleCheckAll}
									/>
								</CustomTableCell>
								<CustomTableCell>{translate("dsi.agents.spawnable.table.title")}</CustomTableCell>
								<CustomTableCell>{translate("dsi.agents.spawnable.table.prompt")}</CustomTableCell>
							</CustomTableRow>
						</CustomTableHead>
						<CustomTableBody>
							{isLoading && (
								<CustomTableRow>
									<CustomTableCell colSpan={3}>{translate("common:loading")}</CustomTableCell>
								</CustomTableRow>
							)}
							{!isLoading &&
								isNonEmptyArray(visibleAgents) &&
								visibleAgents.map((row) => rowRenderer(row))}
							{!isLoading && !isNonEmptyArray(visibleAgents) && (
								<CustomTableRow>
									<CustomTableCell colSpan={3}>{translate("common:no-results")}</CustomTableCell>
								</CustomTableRow>
							)}
						</CustomTableBody>
					</CustomTable>
				</OptionsPanelContainer>
			</SidePanelContent>
		</SidePanel>
	);
}
